/**
 * The product catalogue (data/catalog.json): the aisles of a shop and the products at the level one
 * writes on a shopping list ("Dosentomaten", "Spülmaschinentabs"). Every product has a fixed id;
 * the apps speak to each other in these ids, never in names (docs/kennungen.md).
 */
import data from "../data/catalog.json" with { type: "json" };

/** Which app an aisle belongs to: food (VorratsBuch, SpeisePlan), drink (KellerLog), household goods. */
export type AisleKind = "food" | "drink" | "household";

export interface Aisle {
  /** Fixed: "gewuerze". */
  id: string;
  name: string;
  /** The American English name: "Spices, Oil & Condiments". */
  en: string;
  emoji: string;
  kind: AisleKind;
}

export interface Product {
  /** Fixed: productId of the first name when it was given ("dosentomaten"). */
  id: string;
  /** The usual name first, then synonyms and regional words. */
  names: readonly string[];
  /** The aisle's id: "konserven". */
  aisle: string;
  /** Its own emoji; without one it shows its aisle's (emojiOf). */
  emoji?: string;
  /**
   * The unit a stock of it is counted in, only where it is not what its aisle suggests: "ml" for
   * what is poured (Milch, Sahne, Öl, Essig, Sojasauce). Without it the app goes by the aisle as
   * before (VorratsBuch: g for food, ml for drinks, Stk. for household goods). Not on drinks.
   */
  unit?: "ml";
  /** English names, American first: for someone who knows the food only in English. */
  en?: readonly string[];
  /**
   * For an English name, the German one the shopping list writes, where it is not the first name
   * ("banana" → "Bananen", "skim milk" → "Fettarme Milch"); first from the EinkaufsZettel's catalog-en.ts.
   */
  enAs?: Readonly<Record<string, string>>;
}

/** The aisles in the order of the usual walk through a German supermarket. */
export const AISLES: readonly Aisle[] = data.aisles as Aisle[];
export const PRODUCTS: readonly Product[] = data.products as readonly Product[];
/** The last state that came from the EinkaufsZettel; since 23.10.2026 the catalogue is kept here (H4). */
export const CATALOG_SOURCE: { repo: string; commit: string } = data.source;

const BY_ID = new Map(PRODUCTS.map((p) => [p.id, p]));
const AISLE_BY_ID = new Map(AISLES.map((a) => [a.id, a]));

export const product = (id: unknown): Product | undefined => (typeof id === "string" ? BY_ID.get(id) : undefined);
export const aisle = (id: unknown): Aisle | undefined => (typeof id === "string" ? AISLE_BY_ID.get(id) : undefined);

/** The German name to write for an English one of the product: its own from `enAs`, else the first name. */
export const germanFor = (p: Product, english: string): string => (p.enAs && Object.hasOwn(p.enAs, english) ? p.enAs[english]! : p.names[0]!);

/** The product's emoji, else its aisle's. */
export const emojiOf = (p: Product): string => p.emoji ?? aisle(p.aisle)?.emoji ?? "📦";

/** The aisles of one kind or several, in shop order: aislesOf("food") for VorratsBuch. */
export const aislesOf = (...kinds: AisleKind[]): Aisle[] => AISLES.filter((a) => kinds.includes(a.kind));
